import React, { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  FunctionField,
  Edit,
  SimpleForm,
  TextInput,
  Create,
  Show,
  TabbedShowLayout,
  Tab,
  ShowButton,
  useTranslate,
  required,
  email,
  SelectInput,
  ReferenceField,
  EmailField,
  useRecordContext,
  useRedirect,
  useListContext,
  useNotify,
  TopToolbar,
  Button as RaButton,
  CreateButton,
  FilterLiveSearch,
} from 'react-admin';
import { 
  Chip, 
  Link, 
  Card, 
  CardContent, 
  Stack, 
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Button as MuiButton,
} from '@mui/material';
import { alpha, styled } from "@mui/material/styles";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';

const StyledSimpleForm = styled(SimpleForm)(({ theme }) => ({
    backgroundColor: '#fff',
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '& .MuiFormControl-root': {
        width: '100%',
        maxWidth: '600px',
        marginBottom: '16px'
    },
    '& .MuiInputBase-root': {
        width: '100%'
    }
}));

const StyledList = styled(List)({
  '& .MuiPaper-root': {
    padding: '12px 16px !important',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  '& .MuiToolbar-root': {
    minHeight: '40px !important',
    paddingLeft: 0,
    paddingRight: 0,
    marginBottom: '0px',
  }
});

const StatusField = props => {
  const record = useRecordContext();
  if (!record) return null;

  const getStatusColor = status => {
    switch (status) {
      case 'sent':
        return { color: 'success', label: 'Wysłany' };
      case 'not_sent':
        return { color: 'warning', label: 'Nie wysłany' };
      case 'failed':
        return { color: 'error', label: 'Błąd' };
      default:
        return { color: 'default', label: status };
    }
  };

  const { color, label } = getStatusColor(record.status);

  return (
    <Chip 
      label={label}
      color={color}
      size="small"
    />
  );
};

const EmailConsentChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  if (record.consent_email === true) {
    return <Chip label="Tak" color="success" size="small" />;
  }
  if (record.consent_email === false) {
    return <Chip label="Nie" color="error" size="small" />;
  }
  return <Chip label="Brak danych" color="default" size="small" />;
};

const ProposalNumberField = () => {
  const record = useRecordContext();
  if (!record) return null;
  if (record.proposal_number === null || record.proposal_number === undefined) {
    return <span style={{ color: '#bdbdbd' }}>—</span>;
  }
  return <span>{record.proposal_number}</span>;
};

const BidderField = props => {
  const record = useRecordContext();
  const redirect = useRedirect();
  
  if (!record) return null;

  if (!record.bidder_name) {
    return <span>-</span>;
  }

  const handleClick = (e) => {
    e.preventDefault();
    redirect('show', 'bidders', record.bidder);
  };

  return (
    <Link
      component="button"
      onClick={handleClick}
      underline="hover"
      color="primary"
      style={{
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit'
      }}
    >
      {record.bidder_name}
    </Link>
  );
};

const HtmlField = props => {
  const record = useRecordContext();
  const { source } = props;
  
  if (!record || !record[source]) return null;

  // Clean up the HTML content to normalize line breaks
  const cleanHtml = record[source]
    // First, normalize all <br> variations to <br>
    .replace(/<br\s*\/?>/gi, '<br>')
    // Remove newlines that are adjacent to <br> tags to avoid double spacing
    .replace(/\n\s*<br>/gi, '<br>')
    .replace(/<br>\s*\n/gi, '<br>')
    // Replace multiple consecutive <br> tags with single ones
    .replace(/(<br>\s*){2,}/gi, '<br>')
    // Replace remaining standalone newlines with <br> tags
    .replace(/\n/g, '<br>')
    // Final cleanup of multiple <br> tags
    .replace(/(<br>\s*){2,}/gi, '<br>');

  return (
    <div 
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '24px',
        backgroundColor: '#ffffff',
        minHeight: '500px',
        maxHeight: '80vh',
        overflow: 'auto',
        fontSize: '16px',
        lineHeight: '1.6',
        fontFamily: 'Arial, sans-serif',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        margin: '16px 0'
      }}
      dangerouslySetInnerHTML={{ __html: cleanHtml }}
    />
  );
};

// Export CSV Button
const ExportCSVButton = () => {
  const { filterValues, sort } = useListContext();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const translate = useTranslate();

  const handleExport = async () => {
    setLoading(true);
    try {
      // Build query parameters to match current filters and sorting
      const params = new URLSearchParams();

      // Add filters
      Object.entries(filterValues || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sort?.field) {
        params.append('_sort', sort.field);
        params.append('_order', sort.order?.toLowerCase() || 'desc');
      }

      // Make direct API call to export endpoint
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/email/messages/export/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        // Display error message from backend if available
        const errorMessage = result.error || 'Eksport nieudany';
        notify(errorMessage, { type: 'error' });
        return;
      }
      
      const data = result.data;

      if (!data || data.length === 0) {
        notify('Brak danych do eksportu', {
          type: 'warning',
        });
        return;
      }
      
      // Display warning if export was limited
      if (result.warning) {
        notify(result.warning, { type: 'warning' });
      }
      
      // Get all unique keys from data
      const allKeys = new Set();
      data.forEach(item => {
        Object.keys(item).forEach(key => allKeys.add(key));
      });
      
      // Convert to array and sort
      const headers = Array.from(allKeys).sort();
      
      // Convert data to CSV
      // Use semicolon as delimiter so Excel (Polish/European locale) splits columns correctly
      const delimiter = ';';
      const csvRows = [];

      // Add header row
      csvRows.push(headers.map(h => `"${h}"`).join(delimiter));

      // Add data rows
      data.forEach(item => {
        const values = headers.map(header => {
          const value = item[header];
          if (value === null || value === undefined) {
            return '""';
          }
          // Convert value to string and escape quotes
          const stringValue = String(value).replace(/"/g, '""');
          return `"${stringValue}"`;
        });
        csvRows.push(values.join(delimiter));
      });

      // UTF-8 BOM so Excel recognizes encoding and Polish characters display correctly
      const csvContent = '\uFEFF' + csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `emails_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      notify(`Wyeksportowano ${data.length} wiadomości`, { type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      notify('Błąd podczas eksportu: ' + error.message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <RaButton
      label={loading ? 'Eksportowanie...' : 'Eksportuj do CSV'}
      onClick={handleExport}
      disabled={loading}
      startIcon={<FileDownloadIcon />}
      sx={{
        backgroundColor: '#E31837',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#C41430',
        },
        '& .MuiSvgIcon-root': {
          color: '#ffffff',
        },
      }}
    />
  );
};

// Export Excel Button
const ExportExcelButton = () => {
  const { filterValues, sort } = useListContext();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      Object.entries(filterValues || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      if (sort?.field) {
        params.append('_sort', sort.field);
        params.append('_order', sort.order?.toLowerCase() || 'desc');
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/email/messages/export/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        notify(result.error || 'Eksport nieudany', { type: 'error' });
        return;
      }

      const data = result.data;

      if (!data || data.length === 0) {
        notify('Brak danych do eksportu', { type: 'warning' });
        return;
      }

      if (result.warning) {
        notify(result.warning, { type: 'warning' });
      }

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Emails');
      XLSX.writeFile(workbook, `emails_export_${new Date().toISOString().split('T')[0]}.xlsx`);

      notify(`Wyeksportowano ${data.length} wiadomości`, { type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      notify('Błąd podczas eksportu: ' + error.message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <RaButton
      label={loading ? 'Eksportowanie...' : 'Eksportuj do Excel'}
      onClick={handleExport}
      disabled={loading}
      startIcon={<FileDownloadIcon />}
      sx={{
        backgroundColor: '#E31837',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#C41430',
        },
        '& .MuiSvgIcon-root': {
          color: '#ffffff',
        },
      }}
    />
  );
};

const EmailListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportCSVButton />
    <ExportExcelButton />
  </TopToolbar>
);

const EmailFilterSidebar = () => {
  const translate = useTranslate();
  const { setFilters, filterValues } = useListContext();

  const [filterKey, setFilterKey] = useState(Date.now());
  const [sentDateFrom, setSentDateFrom] = useState(filterValues.sent_at_gte || '');
  const [sentDateTo, setSentDateTo] = useState(filterValues.sent_at_lte || '');
  const [status, setStatus] = useState(filterValues.status || '');
  const [source, setSource] = useState(filterValues.source || '');
  const [proposalNumber, setProposalNumber] = useState(filterValues.proposal_number || '');
  const [templateFilter, setTemplateFilter] = useState(filterValues.template_filter || '');
  const [templates, setTemplates] = useState([]);
  const [consentEmail, setConsentEmail] = useState(
    filterValues.consent_email === undefined ? '' : String(filterValues.consent_email)
  );
  const [consentEmailDateFrom, setConsentEmailDateFrom] = useState(filterValues.consent_email_date_from || '');
  const [consentEmailDateTo, setConsentEmailDateTo] = useState(filterValues.consent_email_date_to || '');
  const [consentEmailWithdrawnFrom, setConsentEmailWithdrawnFrom] = useState(filterValues.consent_email_withdrawn_at_from || '');
  const [consentEmailWithdrawnTo, setConsentEmailWithdrawnTo] = useState(filterValues.consent_email_withdrawn_at_to || '');

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    fetch(`${apiUrl}/email/templates/`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
    })
      .then(r => r.json())
      .then(data => setTemplates(Array.isArray(data) ? data : (data.results || [])))
      .catch(() => {});
  }, []);
  const [taxNumber, setTaxNumber] = useState(filterValues.tax_number || '');

  // Set default filter to last 7 days on initial load
  useEffect(() => {
    if (!filterValues.sent_at_gte && !filterValues.sent_at_lte && !filterValues._initialized) {
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);
      
      const fromDate = sevenDaysAgo.toISOString().split('T')[0];
      const toDate = today.toISOString().split('T')[0];
      
      setSentDateFrom(fromDate);
      setSentDateTo(toDate);
      
      setFilters({
        ...filterValues,
        sent_at_gte: fromDate,
        sent_at_lte: toDate,
        _initialized: true,
      }, {});
    }
  }, []);

  const handleDateChange = (field, value) => {
    if (field === 'from') {
      setSentDateFrom(value);
      const newFilters = { ...filterValues };
      if (value) {
        newFilters.sent_at_gte = value;
      } else {
        delete newFilters.sent_at_gte;
      }
      setFilters(newFilters, {});
    } else {
      setSentDateTo(value);
      const newFilters = { ...filterValues };
      if (value) {
        newFilters.sent_at_lte = value;
      } else {
        delete newFilters.sent_at_lte;
      }
      setFilters(newFilters, {});
    }
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setStatus(value);
    const newFilters = { ...filterValues };
    if (value) {
      newFilters.status = value;
    } else {
      delete newFilters.status;
    }
    setFilters(newFilters, {});
  };

  const handleSourceChange = (event) => {
    const value = event.target.value;
    setSource(value);
    const newFilters = { ...filterValues };
    if (value) {
      newFilters.source = value;
    } else {
      delete newFilters.source;
    }
    setFilters(newFilters, {});
  };

  const handleTemplateFilterChange = (event) => {
    const value = event.target.value;
    setTemplateFilter(value);
    const newFilters = { ...filterValues };
    if (value !== '') {
      newFilters.template_filter = value;
    } else {
      delete newFilters.template_filter;
    }
    setFilters(newFilters, {});
  };

  const handleProposalNumberChange = (e) => {
    const value = e.target.value;
    setProposalNumber(value);
    const newFilters = { ...filterValues };
    if (value !== '') {
      newFilters.proposal_number = value;
    } else {
      delete newFilters.proposal_number;
    }
    setFilters(newFilters, {});
  };

  const handleConsentEmailChange = (event) => {
    const value = event.target.value;
    setConsentEmail(value);
    const newFilters = { ...filterValues };
    if (value !== '') {
      newFilters.consent_email = value;
    } else {
      delete newFilters.consent_email;
    }
    setFilters(newFilters, {});
  };

  const handleConsentDateChange = (field, value) => {
    const newFilters = { ...filterValues };
    if (field === 'given_from') {
      setConsentEmailDateFrom(value);
      if (value) newFilters.consent_email_date_from = value; else delete newFilters.consent_email_date_from;
    } else if (field === 'given_to') {
      setConsentEmailDateTo(value);
      if (value) newFilters.consent_email_date_to = value; else delete newFilters.consent_email_date_to;
    } else if (field === 'withdrawn_from') {
      setConsentEmailWithdrawnFrom(value);
      if (value) newFilters.consent_email_withdrawn_at_from = value; else delete newFilters.consent_email_withdrawn_at_from;
    } else if (field === 'withdrawn_to') {
      setConsentEmailWithdrawnTo(value);
      if (value) newFilters.consent_email_withdrawn_at_to = value; else delete newFilters.consent_email_withdrawn_at_to;
    }
    setFilters(newFilters, {});
  };

  const handleResetFilters = () => {
    setFilterKey(Date.now());
    setSentDateFrom('');
    setSentDateTo('');
    setStatus('');
    setSource('');
    setTaxNumber('');
    setProposalNumber('');
    setTemplateFilter('');
    setConsentEmail('');
    setConsentEmailDateFrom('');
    setConsentEmailDateTo('');
    setConsentEmailWithdrawnFrom('');
    setConsentEmailWithdrawnTo('');

    requestAnimationFrame(() => {
      setFilters({ _initialized: true }, {});
    });
  };

  return (
    <Card sx={{ order: -1, mr: 2, mt: 9, minWidth: 250, maxWidth: 300, boxShadow: 0, borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <FilterLiveSearch key={`subject-${filterKey}`} source="subject" label="Temat" />
          <FilterLiveSearch key={`to_email-${filterKey}`} source="to_email" label="Email odbiorcy" />
          <FilterLiveSearch key={`tax_number-${filterKey}`} source="tax_number" label="NIP" />

          <MuiTextField
            label="Numer propozycji"
            type="number"
            value={proposalNumber}
            onChange={handleProposalNumberChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 1 }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 1 }}
          />

          <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
            Data wysłania
          </div>
          <MuiTextField
            label="Od"
            type="date"
            value={sentDateFrom}
            onChange={(e) => handleDateChange('from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <MuiTextField
            label="Do"
            type="date"
            value={sentDateTo}
            onChange={(e) => handleDateChange('to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Szablon</InputLabel>
            <MuiSelect
              value={templateFilter}
              onChange={handleTemplateFilterChange}
              label="Szablon"
            >
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value="none">Bez szablonu</MenuItem>
              {templates.map(t => (
                <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <MuiSelect
              value={status}
              onChange={handleStatusChange}
              label="Status"
            >
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value="sent">Wysłany</MenuItem>
              <MenuItem value="not_sent">Nie wysłany</MenuItem>
              <MenuItem value="failed">Błąd</MenuItem>
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Źródło</InputLabel>
            <MuiSelect
              value={source}
              onChange={handleSourceChange}
              label="Źródło"
            >
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value="automatic">Automatyczny</MenuItem>
              <MenuItem value="manual">Manualny</MenuItem>
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Zgoda marketingowa</InputLabel>
            <MuiSelect
              value={consentEmail}
              onChange={handleConsentEmailChange}
              label="Zgoda marketingowa"
            >
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value="true">Tak</MenuItem>
              <MenuItem value="false">Nie</MenuItem>
            </MuiSelect>
          </FormControl>

          <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
            Data udzielenia zgody
          </div>
          <MuiTextField
            label="Od"
            type="date"
            value={consentEmailDateFrom}
            onChange={(e) => handleConsentDateChange('given_from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <MuiTextField
            label="Do"
            type="date"
            value={consentEmailDateTo}
            onChange={(e) => handleConsentDateChange('given_to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
            Data wycofania zgody
          </div>
          <MuiTextField
            label="Od"
            type="date"
            value={consentEmailWithdrawnFrom}
            onChange={(e) => handleConsentDateChange('withdrawn_from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <MuiTextField
            label="Do"
            type="date"
            value={consentEmailWithdrawnTo}
            onChange={(e) => handleConsentDateChange('withdrawn_to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <MuiButton
            variant="outlined"
            size="small"
            onClick={handleResetFilters}
            sx={{ mt: 3, mb: 1 }}
            fullWidth
          >
            Wyczyść filtry
          </MuiButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const EmailList = props => {
  const translate = useTranslate();
  return (
    <StyledList 
      {...props} 
      perPage={25}
      sort={{ field: 'created_at', order: 'DESC' }}
      actions={<EmailListActions />}
      aside={<EmailFilterSidebar />}
    >
      <Datagrid 
        rowClick="show"
        bulkActionButtons={false}
      >
        <TextField source="id" label={translate('resources.email.messages.fields.id')} />
        <TextField source="subject" label={translate('resources.email.messages.fields.subject')} />
        <EmailField source="to_email" label={translate('resources.email.messages.fields.to_email')} />
        <BidderField
          label={translate('resources.email.messages.fields.bidder', 'Oferent')}
        />
        <TextField source="bidder_tax_number" label="NIP" />
        <StatusField source="status" label={translate('resources.email.messages.fields.status')} />
        <TextField source="source" label={translate('resources.email.messages.fields.source')} />
        <TextField source="template_name" label="Szablon" />
        <DateField source="created_at" label={translate('resources.email.messages.fields.created_at')} showTime />
        <DateField source="sent_at" label={translate('resources.email.messages.fields.sent_at')} showTime />
        <ProposalNumberField label="Numer propozycji" sortBy="proposal_number" />
        <FunctionField
          source="consent_email"
          label="Zgoda marketingowa"
          render={() => <EmailConsentChip />}
        />
        <DateField source="consent_email_date" label="Data udzielenia zgody" showTime />
        <DateField source="consent_email_withdrawn_at" label="Data wycofania zgody" showTime />
        <ShowButton label={translate('ra.action.show')} />
      </Datagrid>
    </StyledList>
  );
};

export const EmailShow = props => {
  const translate = useTranslate();
  return (
    <Show {...props}>
      <TabbedShowLayout>
        <Tab label={translate('resources.email.messages.tabs.details')}>
          <TextField source="id" label={translate('resources.email.messages.fields.id')} />
          <TextField source="subject" label={translate('resources.email.messages.fields.subject')} />
          <EmailField source="to_email" label={translate('resources.email.messages.fields.to_email')} />
          <BidderField 
            label={translate('resources.email.messages.fields.bidder', 'Oferent')}
          />
          <TextField source="cc" label={translate('resources.email.messages.fields.cc')} />
          <TextField source="bcc" label={translate('resources.email.messages.fields.bcc')} />
          <StatusField source="status" label={translate('resources.email.messages.fields.status')} />
          <TextField source="source" label={translate('resources.email.messages.fields.source')} />
          <TextField source="template_name" label="Szablon" />
          <TextField source="created_by_username" label={translate('resources.email.messages.fields.created_by_username')} />
          <DateField source="created_at" label={translate('resources.email.messages.fields.created_at')} showTime />
          <DateField source="sent_at" label={translate('resources.email.messages.fields.sent_at')} showTime />
          <ProposalNumberField label="Numer propozycji" />
          <TextField source="error_message" label={translate('resources.email.messages.fields.error_message')} />
        </Tab>
        <Tab label={translate('resources.email.messages.tabs.body')}>
          <HtmlField source="body" label={translate('resources.email.messages.fields.body')} />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

export const EmailCreate = props => {
  const translate = useTranslate();
  return (
    <Create {...props}>
      <StyledSimpleForm>
        <TextInput source="subject" label={translate('resources.email.messages.fields.subject')} validate={required()} />
        <TextInput source="to_email" label={translate('resources.email.messages.fields.to_email')} validate={[required(), email()]} />
        <TextInput source="cc" label={translate('resources.email.messages.fields.cc')} />
        <TextInput source="bcc" label={translate('resources.email.messages.fields.bcc')} />
        <TextInput 
          source="body" 
          label={translate('resources.email.messages.fields.body')} 
          validate={required()} 
          multiline 
          rows={10}
        />
      </StyledSimpleForm>
    </Create>
  );
};

export const EmailEdit = props => {
  const translate = useTranslate();
  return (
    <Edit {...props}>
      <StyledSimpleForm>
        <TextInput source="subject" label={translate('resources.email.messages.fields.subject')} validate={required()} />
        <TextInput source="to_email" label={translate('resources.email.messages.fields.to_email')} validate={[required(), email()]} />
        <TextInput source="cc" label={translate('resources.email.messages.fields.cc')} />
        <TextInput source="bcc" label={translate('resources.email.messages.fields.bcc')} />
        <TextInput 
          source="body" 
          label={translate('resources.email.messages.fields.body')} 
          validate={required()} 
          multiline 
          rows={10}
        />
        <TextField source="source" label={translate('resources.email.messages.fields.source')} />
        <SelectInput 
          source="status" 
          label={translate('resources.email.messages.fields.status')} 
          choices={[
            { id: 'not_sent', name: 'Nie wysłany' },
            { id: 'sent', name: 'Wysłany' },
            { id: 'failed', name: 'Błąd' },
          ]}
        />
      </StyledSimpleForm>
    </Edit>
  );
}; 